import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CpuChipIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { StockProduct, ProductCategory, IntakeParser, AttributeSchemaField } from '../../types/inventory';
import { CATEGORY_LABELS } from '../../types/inventory';

const PARSER_LABELS: Record<IntakeParser, string> = {
  soten_qr: 'Soten QR',
  raw_serial: 'Raw Serial',
  ean: 'EAN Barcode',
};

interface ProductFormState {
  sku: string;
  name: string;
  manufacturer: string;
  category: ProductCategory;
  hasBuildStep: boolean;
  intakeParser: IntakeParser;
  attributesSchema: AttributeSchemaField[];
}

const EMPTY_FORM: ProductFormState = {
  sku: '',
  name: '',
  manufacturer: '',
  category: 'tablet',
  hasBuildStep: false,
  intakeParser: 'raw_serial',
  attributesSchema: [],
};

function ProductModal({
  product,
  onClose,
}: {
  product: StockProduct | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ProductFormState>(
    product
      ? {
          sku: product.sku,
          name: product.name,
          manufacturer: product.manufacturer ?? '',
          category: product.category,
          hasBuildStep: product.hasBuildStep,
          intakeParser: product.intakeParser,
          attributesSchema: product.attributesSchema,
        }
      : EMPTY_FORM
  );
  const [attrKey, setAttrKey] = useState('');
  const [attrLabel, setAttrLabel] = useState('');
  const [attrRequired, setAttrRequired] = useState(false);

  const createMutation = useMutation({
    mutationFn: () =>
      api.adminCreateStockProduct({
        ...form,
        manufacturer: form.manufacturer || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'products'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.adminUpdateStockProduct(product!.id, {
        name: form.name,
        manufacturer: form.manufacturer || null,
        category: form.category,
        hasBuildStep: form.hasBuildStep,
        intakeParser: form.intakeParser,
        attributesSchema: form.attributesSchema,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'products'] });
      onClose();
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function addAttr() {
    if (!attrKey.trim() || !attrLabel.trim()) return;
    setForm(f => ({
      ...f,
      attributesSchema: [...f.attributesSchema, { key: attrKey.trim(), label: attrLabel.trim(), required: attrRequired }],
    }));
    setAttrKey('');
    setAttrLabel('');
    setAttrRequired(false);
  }

  function removeAttr(idx: number) {
    setForm(f => ({ ...f, attributesSchema: f.attributesSchema.filter((_, i) => i !== idx) }));
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {product ? 'Edit Product' : 'New Product'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">SKU *</label>
              <input
                value={form.sku}
                onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
                disabled={!!product}
                placeholder="SCOUT-G10"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Category *</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value as ProductCategory }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
              >
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Name *</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Scout Tablet G10"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Manufacturer</label>
            <input
              value={form.manufacturer}
              onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))}
              placeholder="Soten"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Barcode Parser</label>
              <select
                value={form.intakeParser}
                onChange={e => setForm(f => ({ ...f, intakeParser: e.target.value as IntakeParser }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
              >
                {Object.entries(PARSER_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.hasBuildStep}
                  onChange={e => setForm(f => ({ ...f, hasBuildStep: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-sm text-slate-300">Requires gold build</span>
              </label>
            </div>
          </div>

          {/* Attribute Schema Builder */}
          <div>
            <label className="block text-xs text-slate-400 mb-2">Attributes captured on scan</label>
            <div className="space-y-1 mb-2">
              {form.attributesSchema.map((a, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-700/50 rounded px-2 py-1">
                  <span className="text-xs font-mono text-cyan-400 flex-1">{a.key}</span>
                  <span className="text-xs text-slate-300 flex-1">{a.label}</span>
                  {a.required && <span className="text-xs text-orange-400">required</span>}
                  <button onClick={() => removeAttr(i)} className="text-slate-500 hover:text-red-400">
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={attrKey}
                onChange={e => setAttrKey(e.target.value)}
                placeholder="key"
                className="w-24 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-xs focus:border-cyan-500 focus:outline-none font-mono"
              />
              <input
                value={attrLabel}
                onChange={e => setAttrLabel(e.target.value)}
                placeholder="Label"
                className="flex-1 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-xs focus:border-cyan-500 focus:outline-none"
              />
              <label className="flex items-center gap-1 text-xs text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attrRequired}
                  onChange={e => setAttrRequired(e.target.checked)}
                  className="w-3 h-3"
                />
                req
              </label>
              <button
                onClick={addAttr}
                className="px-2 py-1.5 bg-slate-600 hover:bg-slate-500 text-white rounded text-xs"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={() => product ? updateMutation.mutate() : createMutation.mutate()}
            disabled={isPending || !form.sku || !form.name}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {isPending ? 'Saving…' : product ? 'Save Changes' : 'Create Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminInventoryProductsPage() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<StockProduct | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['admin', 'inventory', 'products', showInactive],
    queryFn: () => api.adminListStockProducts(showInactive),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.adminDeleteStockProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'products'] }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.adminUpdateStockProduct(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'products'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Product Catalog</h1>
          <p className="text-slate-400 mt-1">Hardware types and their intake configuration</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              className="w-4 h-4 rounded border-slate-600"
            />
            Show inactive
          </label>
          <button
            onClick={() => { setEditTarget(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            New Product
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : products.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
          <CpuChipIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No products yet. Create your first product type to start tracking inventory.</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-4 py-3 text-slate-400 font-medium">SKU</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Name</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Manufacturer</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Category</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Parser</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Build Step</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {products.map(p => (
                <tr key={p.id} className="hover:bg-slate-700/20 transition-colors">
                  <td className="px-4 py-3 font-mono text-cyan-400 text-xs">{p.sku}</td>
                  <td className="px-4 py-3 text-white">{p.name}</td>
                  <td className="px-4 py-3 text-slate-300">{p.manufacturer ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-300">{CATEGORY_LABELS[p.category]}</td>
                  <td className="px-4 py-3 text-slate-300">{PARSER_LABELS[p.intakeParser]}</td>
                  <td className="px-4 py-3">
                    {p.hasBuildStep ? (
                      <CheckCircleIcon className="w-4 h-4 text-green-400" />
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${p.isActive ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => { setEditTarget(p); setShowModal(true); }}
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => toggleActiveMutation.mutate({ id: p.id, isActive: !p.isActive })}
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                        title={p.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {p.isActive ? <XCircleIcon className="w-3.5 h-3.5" /> : <CheckCircleIcon className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete product ${p.sku}? This will fail if stock items exist.`)) {
                            deleteMutation.mutate(p.id);
                          }
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ProductModal
          product={editTarget}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
        />
      )}
    </div>
  );
}
